import {useChatsOfCampaignQuery} from "@/api/chatApi";
import {chatSortAtom} from "@/store/jotaiAtoms";
import {useParams, useSearch} from "@tanstack/react-router";
import {useAtomValue} from "jotai";
import {useEffect} from "react";
import {useMedia} from "react-use";
import {z} from "zod";

export const useChatMobileUI = () => {
  const {influencerUserId} = useParams({strict: false});
  const {isBulk} = useSearch({strict: false});
  const isMobileView = useMedia("(max-width: 768px)");

  const selectedMobileUI: "overview" | "chat" | "newMessage" | "desktop" = !isMobileView
    ? "desktop"
    : isBulk
      ? "chat"
      : !Number(influencerUserId)
        ? "overview"
        : "chat";

  return {selectedMobileUI, isMobileView};
};

export const useIsNeedsReplyLane = () => {
  const {campaignId} = useParams({strict: false});
  const {isNeedsReply} = useSearch({strict: false});

  return isNeedsReply || !campaignId;
};

export const useIsCampaignChatClosed = () => {
  const {campaignId, influencerUserId} = useParams({strict: false});
  const {search} = useSearch({strict: false});
  const sort = useAtomValue(chatSortAtom);
  const {query: chatsOfCampaignQuery} = useChatsOfCampaignQuery({
    campaignId: Number(campaignId),
    search,
    sort,
  });
  const isCampaignClosed = influencerUserId
    ? (chatsOfCampaignQuery.data?.results.find(chat => chat.user.id === Number(influencerUserId))?.has_completed ??
      false)
    : false;

  useEffect(() => {
    if (
      !chatsOfCampaignQuery.data?.results.find(chat => chat.user.id === Number(influencerUserId)) &&
      chatsOfCampaignQuery.hasNextPage &&
      !chatsOfCampaignQuery.isFetching
    ) {
      chatsOfCampaignQuery.fetchNextPage();
    }
  }, [chatsOfCampaignQuery.data, chatsOfCampaignQuery.hasNextPage, chatsOfCampaignQuery.isFetching, influencerUserId]);

  return isCampaignClosed;
};

const stringToBoolean = (value: string | undefined) => {
  return value?.toLowerCase() === "true";
};

export const chatUrlSchema = z.object({
  isBulk: z.string().optional().transform(stringToBoolean),
  search: z.string().optional(),
});

export const useFileType = (
  filename: string | null | undefined,
): {
  type: "image" | "video" | "zip" | "pdf" | "unknown";
  ext: string | null | undefined;
} => {
  if (!filename) return {type: "unknown", ext: null};
  const ext = filename.split("?")[0].split(".").pop()?.toLowerCase();
  switch (ext) {
    case "jpg":
    case "jpeg":
    case "gif":
    case "bmp":
    case "png":
      return {type: "image", ext};
    case "m4v":
    case "avi":
    case "mpg":
    case "mp4":
    case "mov":
      return {type: "video", ext};
    case "zip":
      return {type: "zip", ext};
    case "pdf":
      return {type: "pdf", ext};
    default:
      return {type: "unknown", ext};
  }
};
